// src/components/booking/firebase/holds.js
import { getRtdb } from "../../../utils/firebase";
import { ref, set, get, remove, onValue } from "firebase/database";
import { TTL } from "../../../theme";

export const HOLD_TTL_MS  = TTL.HOLD_MS;
const        HOLD_TICK_MS = 10 * 1000;

export function holdPath(dateStr, court, slotId) {
  const courtKey = court.replace(/\s+/g, "_");
  return `holds/${dateStr}/${courtKey}/${slotId}`;
}

export async function cleanupStaleHoldsGlobally() {
  const rtdb = getRtdb();
  const holdsRoot = ref(rtdb, "holds");
  const snapshot  = await get(holdsRoot);
  if (!snapshot.exists()) return;

  const now         = Date.now();
  const cleanupRefs = [];

  snapshot.forEach((dateSnap) => {
    dateSnap.forEach((courtSnap) => {
      courtSnap.forEach((slotSnap) => {
        const data = slotSnap.val();
        if (data && typeof data.heldAt === "number" && now - data.heldAt >= HOLD_TTL_MS) {
          cleanupRefs.push(slotSnap.ref);
        }
      });
    });
  });

  await Promise.all(cleanupRefs.map((r) => remove(r)));
  if (cleanupRefs.length > 0) {
    console.debug(`[holds] Cleaned up ${cleanupRefs.length} stale hold(s).`);
  }
}

export async function holdSlot(dateStr, court, slotId, sessionId) {
  const path     = holdPath(dateStr, court, slotId);
  const slotRef  = ref(getRtdb(), path);
  const existing = await get(slotRef);

  if (existing.exists()) {
    const data    = existing.val();
    const isStale = Date.now() - data.heldAt >= HOLD_TTL_MS;
    const isMine  = data.sessionId === sessionId;
    if (!isStale && !isMine) return false;
  }

  await set(slotRef, { sessionId, heldAt: Date.now() });
  return true;
}

export async function releaseSlot(dateStr, court, slotId, sessionId) {
  const path    = holdPath(dateStr, court, slotId);
  const slotRef = ref(getRtdb(), path);
  const snap    = await get(slotRef);
  if (!snap.exists()) return;
  if (snap.val().sessionId !== sessionId) return;
  await remove(slotRef);
}

export async function releaseAllHolds(dateStr, court, slotIds, sessionId) {
  await Promise.all(slotIds.map((slotId) => releaseSlot(dateStr, court, slotId, sessionId)));
}

export function subscribeToHolds(dateStr, court, callback) {
  const rtdb = getRtdb();
  const courtKey = court.replace(/\s+/g, "_");
  const holdsRef = ref(rtdb, `holds/${dateStr}/${courtKey}`);
  let latestRaw  = {};
  let stopped = false;
  let retryTimer = null;
  let unsub = null;

  function processSnapshot(raw) {
    const now    = Date.now();
    const active = {};
    Object.entries(raw).forEach(([slotId, data]) => {
      if (!data || typeof data.heldAt !== "number") return;
      if (now - data.heldAt >= HOLD_TTL_MS) {
        remove(ref(rtdb, holdPath(dateStr, court, slotId)));
      } else {
        active[slotId] = data;
      }
    });
    callback(active);
  }

  function attach() {
    unsub = onValue(
      holdsRef,
      (snapshot) => {
        latestRaw = snapshot.val() || {};
        processSnapshot(latestRaw);
      },
      (err) => {
        // holds is public-read (.read: true), so a permission error here
        // is always transient SDK/connection noise - never a genuine deny.
        // Safe to just retry.
        console.warn("[holds] listen error, retrying:", err.message);
        if (!stopped) retryTimer = setTimeout(attach, 800);
      }
    );
  }
  attach();

  const intervalId = setInterval(() => {
    if (Object.keys(latestRaw).length > 0) processSnapshot(latestRaw);
  }, HOLD_TICK_MS);

  return () => {
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    unsub?.();
    clearInterval(intervalId);
  };
}